import React, { useState, useEffect } from 'react';
import {
  Capa,
  CapaDashboardStats,
  CapaStatus,
  CapaPriority,
  CapaType,
} from './types';
import { capaService } from './capaService';
import { CapaListTable } from './components/CapaListTable';
import { CapaDetailModal } from './components/CapaDetailModal';
import { CreateCapaModal } from './components/CreateCapaModal';

export const CapasPage: React.FC = () => {
  const [filterStatus, setFilterStatus] = useState<'ALL' | CapaStatus>('ALL');
  const [filterPriority, setFilterPriority] = useState<'ALL' | CapaPriority>('ALL');
  const [filterType, setFilterType] = useState<'ALL' | CapaType>('ALL');

  const [stats, setStats] = useState<CapaDashboardStats | null>(null);
  const [capas, setCapas] = useState<Capa[]>([]);
  const [loading, setLoading] = useState(false);

  // Modals
  const [selectedCapa, setSelectedCapa] = useState<Capa | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [statsData, capasData] = await Promise.all([
        capaService.getDashboardStats(),
        capaService.getCapas(),
      ]);
      setStats(statsData);
      setCapas(capasData);
    } catch (err) {
      console.error('Error loading CAPA data:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Title & Header */}
      <div className="page-header-banner">
        <div className="badge">QUALITY MANAGEMENT</div>
        <h1>Corrective & Preventive Action (CAPA)</h1>
        <p>End-to-end case resolution: Immediate containment, 5-Why root cause analysis, central action linkage, independent verification (SoD), and closed-loop control.</p>
      </div>

      {/* KPI Stats Grid */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total CAPAs</div>
            <div className="stat-value">{stats.totalCapas}</div>
            <div className="stat-sub">Registered cases</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Open Cases</div>
            <div className="stat-value" style={{ color: '#0284c7' }}>{stats.openCapas}</div>
            <div className="stat-sub">Active workflow</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Critical</div>
            <div className="stat-value" style={{ color: '#ef4444' }}>{stats.criticalCapas}</div>
            <div className="stat-sub">High priority</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Overdue</div>
            <div className="stat-value" style={{ color: '#e11d48' }}>{stats.overdueCapas}</div>
            <div className="stat-sub">Past target date</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Implementation</div>
            <div className="stat-value" style={{ color: '#d97706' }}>{stats.implementationInProgress}</div>
            <div className="stat-sub">Action phase</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Effectiveness</div>
            <div className="stat-value" style={{ color: '#0d9488' }}>{stats.effectivenessMonitoring}</div>
            <div className="stat-sub">Monitoring cycle</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Closed</div>
            <div className="stat-value" style={{ color: '#10b981' }}>{stats.closedCapas}</div>
            <div className="stat-sub">Completed cycle</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Systemic</div>
            <div className="stat-value" style={{ color: '#7c3aed' }}>{stats.systemicCount}</div>
            <div className="stat-sub">Organizational root</div>
          </div>
        </div>
      )}

      {/* Main Table */}
      <CapaListTable
        capas={capas}
        loading={loading}
        filterStatus={filterStatus}
        filterPriority={filterPriority}
        filterType={filterType}
        onFilterStatusChange={setFilterStatus}
        onFilterPriorityChange={setFilterPriority}
        onFilterTypeChange={setFilterType}
        onSelectCapa={(c) => setSelectedCapa(c)}
        onOpenCreate={() => setShowCreateModal(true)}
      />

      {/* Create Modal */}
      {showCreateModal && (
        <CreateCapaModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            loadAllData();
          }}
        />
      )}

      {/* Detail / Management Modal */}
      {selectedCapa && (
        <CapaDetailModal
          capa={selectedCapa}
          onClose={() => setSelectedCapa(null)}
          onRefresh={() => {
            loadAllData();
          }}
        />
      )}
    </div>
  );
};
